// src/components/admin/AdminLayout.js - Enhanced with better portal switching
import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import {
  Users, Server, Key, Home, Settings, LogOut,
  Menu, X, ChevronDown, Shield, Database,
  Search, User
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import PortalSwitcher from '../PortalSwitcher';

const AdminLayout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('dashboard');
  const { user, activeEnvironment, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const pathParts = path.split('/');
    const section = pathParts[pathParts.length - 1];
    if (section && section !== 'admin') {
      setActiveSection(section);
    } else {
      setActiveSection('dashboard');
    }
  }, [location]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);

  // Navigation items
  const navItems = [
    { id: 'dashboard', name: 'Dashboard', icon: Home, path: '/admin/dashboard' },
    { id: 'users', name: 'Users', icon: Users, path: '/admin/users' },
    { id: 'environments', name: 'Environments', icon: Server, path: '/admin/environments' },
    { id: 'authorization', name: 'Authorization', icon: Key, path: '/admin/authorization' },
    { id: 'settings', name: 'Settings', icon: Settings, path: '/admin/settings' }
  ];

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Implement search functionality here
      logger.log('Searching for:', searchQuery);
      // For demo purposes, just navigate to the dashboard
      setSearchQuery('');
    }
  };

  // Handle navigation
  const navigateTo = (path) => {
    navigate(path);
  };

  // If user data is not loaded yet
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar for larger screens */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64 bg-indigo-800 text-white">
          <div className="flex items-center justify-between h-16 border-b border-indigo-700 px-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-white" />
              <span className="ml-2 text-xl font-semibold">Admin Portal</span>
            </div>
          </div>
          
          {/* Navigation Items */}
          <div className="flex-grow overflow-y-auto pt-5 pb-4">
            <nav className="mt-5 px-2 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-md ${
                    activeSection === item.id
                      ? 'bg-indigo-700 text-white'
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`}
                  onClick={() => {
                    setActiveSection(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          
          {/* Environment Information */}
          {activeEnvironment && (
            <div className="p-4 bg-indigo-900">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-indigo-200">Active Environment</h3>
                  <p className="text-sm text-indigo-100 font-semibold mt-1">
                    {activeEnvironment.name}
                  </p>
                </div>
                <div className={`h-3 w-3 rounded-full ${
                  activeEnvironment.type === 'Production' 
                    ? 'bg-green-400' 
                    : activeEnvironment.type === 'Sandbox' 
                      ? 'bg-blue-400' 
                      : 'bg-gray-400'
                }`}>
                </div>
              </div>
            </div>
          )}
          
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top Navigation Bar */}
        <header className="bg-white shadow-sm z-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                {/* Mobile menu button */}
                <button
                  onClick={toggleMobileMenu}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 md:hidden focus:outline-none"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
                
                {/* Admin page title - desktop */}
                <div className="hidden md:block ml-4">
                  <h1 className="text-xl font-semibold text-gray-900">
                    {navItems.find(item => item.id === activeSection)?.name || 'Dashboard'}
                  </h1>
                </div>
                
                {/* Admin page title - mobile */}
                <div className="md:hidden flex items-center ml-2">
                  <Shield className="h-6 w-6 text-indigo-600" />
                  <span className="ml-2 text-lg font-semibold">
                    {navItems.find(item => item.id === activeSection)?.name || 'Admin'}
                  </span>
                </div>
              </div>
              
              {/* Search form - desktop */}
              <div className="hidden md:flex-1 md:flex md:max-w-md md:mx-auto md:items-center md:justify-center">
                <form className="w-full" onSubmit={handleSearch}>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Search users, environments..."
                    />
                  </div>
                </form>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Environment indicator */}
                {activeEnvironment && (
                  <div className="hidden md:flex items-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      (activeEnvironment.type || '').toLowerCase() === 'production'
                        ? 'bg-green-100 text-green-800'
                        : (activeEnvironment.type || '').toLowerCase() === 'sandbox'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      {activeEnvironment.name || 'Unknown'}
                    </span>
                  </div>
                )}

                {/* User dropdown */}
                <div className="relative">
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center gap-2 text-sm rounded-full focus:outline-none"
                  >
                    <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
                      <span className="text-sm font-semibold text-white">{user.name?.charAt(0) || 'U'}</span>
                    </div>
                    <span className="hidden md:flex md:items-center gap-1">
                      <span className="text-sm text-gray-700">{user.name || 'User'}</span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </span>
                  </button>

                  {isUserMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1" role="menu">
                        <div className="block px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate" title={user.email}>{user.email}</p>
                        </div>
                        <button
                          onClick={() => navigate('/portal')}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <div className="flex items-center">
                            <Database className="mr-2 h-4 w-4 text-green-500" />
                            Switch to User Portal
                          </div>
                        </button>
                        <Link
                          to="/admin/settings"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <div className="flex items-center">
                            <Settings className="mr-2 h-4 w-4 text-gray-500" />
                            Settings
                          </div>
                        </Link>
                        <button
                          onClick={logout}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
                        >
                          <div className="flex items-center">
                            <LogOut className="mr-2 h-4 w-4 text-gray-500" />
                            Sign out
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-indigo-800 text-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    activeSection === item.id
                      ? 'bg-indigo-700 text-white'
                      : 'text-indigo-100 hover:bg-indigo-600'
                  }`}
                  onClick={() => {
                    setActiveSection(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <div className="flex items-center">
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </div>
                </Link>
              ))}
              
              <div className="px-3 py-2 mt-2">
                <PortalSwitcher currentPortal="admin" className="w-full" />
              </div>
            </div>
            
            {/* Mobile search */}
            <div className="px-4 py-3 border-t border-indigo-700">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-indigo-300" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-indigo-600 rounded-md leading-5 bg-indigo-700 text-indigo-100 placeholder-indigo-300 focus:outline-none focus:bg-indigo-600 focus:ring-0 focus:placeholder-indigo-300 focus:text-white sm:text-sm"
                    placeholder="Search..."
                  />
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <Outlet />
        </main>
        
        {/* Footer */}
        <footer className="bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
            <p className="text-sm text-gray-500 text-center">
              &copy; 2025 SiriusConnect365. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;